import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Eye, Pause, Play, RotateCcw, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  CHARLES_PRESSURE_KPA,
  EXPLORER_DEFAULTS,
  EXPLORER_RANGES,
  GAS_PREDICTIONS,
  KELVIN_OFFSET,
  formatSig,
  idealVolumeL,
  type ExplorerState,
  type GasMode,
} from './gasLaws';
import { GasGraph, ParticleBox } from './GasLawsVisuals';
import { ToolSlider } from './ToolSlider';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

function withDerivedVolume(mode: GasMode, state: ExplorerState): ExplorerState {
  return mode === 'charles'
    ? { ...state, volumeL: idealVolumeL(state.nMol, state.temperatureK, CHARLES_PRESSURE_KPA) }
    : state;
}

const FIXED_COPY: Record<GasMode, string> = {
  boyle: 'Fixed: n = 0.200 mol and T = 298.15 K (25 °C). Move the piston to change the volume.',
  charles: `Fixed: n = 0.200 mol and p = ${CHARLES_PRESSURE_KPA} kPa. The piston moves freely, so the volume follows the temperature.`,
  ideal:
    'The container is rigid at the chosen volume. Change any of n, T or V and watch p = nRT ÷ V.',
};

export function GasLawsExplorer({ mode }: { mode: GasMode }) {
  const reducedMotion = usePrefersReducedMotion();
  const [state, setState] = useState<ExplorerState>(() =>
    withDerivedVolume(mode, EXPLORER_DEFAULTS[mode]),
  );
  const [paused, setPaused] = useState(false);
  const [choice, setChoice] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [celsius, setCelsius] = useState(false);
  useScienceTutorSection('GasLawsExplorer', { state, choice, revealed, celsius });
  const prediction = GAS_PREDICTIONS[mode];
  const chosen = prediction.options.find((option) => option.id === choice);

  const update = (change: Partial<ExplorerState>) =>
    setState((current) => withDerivedVolume(mode, { ...current, ...change }));

  const reset = () => {
    setState(withDerivedVolume(mode, EXPLORER_DEFAULTS[mode]));
    setChoice('');
    setRevealed(false);
    setCelsius(false);
  };

  return (
    <section
      aria-label="Gas model explorer"
      className="grid gap-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:p-8"
    >
      <div className="min-w-0 space-y-5">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
            Particle model
          </p>
          <p className="mt-2 text-sm font-medium leading-6 text-slate2">{FIXED_COPY[mode]}</p>
        </div>
        <ParticleBox mode={mode} state={state} running={!paused && !reducedMotion} />
        <div className="grid gap-4">
          {mode !== 'charles' ? (
            <ToolSlider
              label="Volume"
              value={state.volumeL}
              min={EXPLORER_RANGES.volumeL.min}
              max={EXPLORER_RANGES.volumeL.max}
              step={EXPLORER_RANGES.volumeL.step}
              display={`${formatSig(state.volumeL, 3)} L`}
              accent="accent-brand-sky"
              onChange={(volumeL) => update({ volumeL })}
            />
          ) : null}
          {mode !== 'boyle' ? (
            <ToolSlider
              label="Temperature"
              value={state.temperatureK}
              min={EXPLORER_RANGES.temperatureK.min}
              max={EXPLORER_RANGES.temperatureK.max}
              step={EXPLORER_RANGES.temperatureK.step}
              display={`${formatSig(state.temperatureK, 4)} K (${formatSig(state.temperatureK - KELVIN_OFFSET, 4)} °C)`}
              onChange={(temperatureK) => update({ temperatureK })}
            />
          ) : null}
          {mode === 'ideal' ? (
            <ToolSlider
              label="Amount of gas"
              value={state.nMol}
              min={EXPLORER_RANGES.nMol.min}
              max={EXPLORER_RANGES.nMol.max}
              step={EXPLORER_RANGES.nMol.step}
              display={`${formatSig(state.nMol, 2)} mol`}
              accent="accent-brand-mint"
              onChange={(nMol) => update({ nMol: Math.round(nMol * 100) / 100 })}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPaused((current) => !current)}
            disabled={reducedMotion}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink-soft disabled:opacity-50"
          >
            {paused || reducedMotion ? (
              <Play className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Pause className="h-4 w-4" aria-hidden="true" />
            )}
            {reducedMotion ? 'Motion reduced' : paused ? 'Play particles' : 'Pause particles'}
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink-soft"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset explorer
          </button>
        </div>
      </div>

      <div className="min-w-0 space-y-4">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Predict the graph
        </p>
        <h2 className="text-xl font-extrabold text-ink">{prediction.question}</h2>
        <div className="grid gap-2" role="group" aria-label="Graph prediction">
          {prediction.options.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={choice === option.id}
              disabled={revealed}
              onClick={() => setChoice(option.id)}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-not-allowed ${
                choice === option.id
                  ? 'border-brand-coral bg-wash-coral text-ink'
                  : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!choice || revealed}
          onClick={() => setRevealed(true)}
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Eye className="h-4 w-4" aria-hidden="true" /> Reveal the model graph
        </button>
        {mode !== 'boyle' ? (
          <div
            className="inline-flex rounded-full bg-surface p-1"
            role="group"
            aria-label="Temperature axis"
          >
            {[false, true].map((useCelsius) => (
              <button
                key={String(useCelsius)}
                type="button"
                aria-pressed={celsius === useCelsius}
                onClick={() => setCelsius(useCelsius)}
                className={`rounded-full px-3 py-1.5 text-xs font-extrabold ${celsius === useCelsius ? 'bg-ink text-white' : 'text-slate2'}`}
              >
                {useCelsius ? 'Celsius axis' : 'Kelvin axis'}
              </button>
            ))}
          </div>
        ) : null}
        <div className="rounded-[1.5rem] border border-hairline bg-surface p-3">
          <GasGraph mode={mode} state={state} revealed={revealed} celsius={celsius} />
        </div>
        {revealed && chosen ? (
          <div
            role="status"
            className={`rounded-[1.5rem] border p-4 ${
              chosen.correct
                ? 'border-brand-mint/45 bg-wash-mint'
                : 'border-brand-coral/45 bg-wash-coral'
            }`}
          >
            <p className="flex items-center gap-2 font-extrabold text-ink">
              {chosen.correct ? (
                <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
              ) : (
                <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
              )}
              {chosen.correct
                ? 'Your prediction matches the model'
                : 'The model shows something different'}
            </p>
            <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{chosen.feedback}</p>
            {mode !== 'boyle' ? (
              <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                {celsius
                  ? 'On the Celsius axis the line does not pass through zero, so V is not proportional to °C. The dashed extension meets zero volume at −273.15 °C.'
                  : 'Switch to the Celsius axis to see why °C values do not give a proportional relationship.'}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
